import type { AnalysisReport, Section } from '@rb/ui'
import raw from './analyse_hotel_a.json'

// ─────────────────────────────────────────────────────────────────────────────
// DEMO DATASET — anonymized hotel energy analysis ("Hotel A").
//
// Drives the public, embeddable /demo page (DemoEmbed.tsx). Fully hardcoded:
// the demo makes no API / D1 / auth calls.
//
// The exported JSON (analyse_hotel_a.json) is keyed by the DB column names
// (analysis / proposal / roi / financing) and holds the same block schema as
// @rb/ui types/report.ts. The demo's AnalysisReport uses the part keys
// (analyse / vorschlag / roi / finanzierung), so we remap below.
//
// The JSON is a template: two sections (ROI "Kapitaldienstfähigkeit" and
// Finanzierung "Investition & Finanzierung") carry {{placeholders}} meant to be
// filled per customer. For this public demo we substitute illustrative,
// internally consistent figures (DEMO_FILL) so nothing renders as raw {{…}}.
// Values track the real ROI figures already in the report (Energiekosten Ist
// ≈ 178.900 €/J, Einsparung ≈ 12.000–20.900 €/J).
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_FILL: Record<string, string> = {
  // ROI · Kapitaldienstfähigkeit
  cashflow_konservativ_eur_jahr: '12.000',
  cashflow_realistisch_eur_jahr: '20.900',
  payback_jahre: '4,0',
  irr_roi_prozent: '18–24',
  kapitaldienst_eur_jahr: '2.900',
  cashflow_kapitaldienst_faktor: '4,1×',
  // Finanzierung · Investition & Finanzierung
  gesamtinvestition_eur: '48.000',
  foerderfaehige_kosten_eur: '38.000',
  foerderungen_eur: '12.000',
  eigenmittel_eur: '18.000',
  eigenmittel_quote_prozent: '38',
  kreditbedarf_eur: '18.000',
  kredit_laufzeit_jahre: '7',
  tilgungsart_beschreibung: 'annuitätisch, gleichbleibende Raten',
  sicherheiten_kurzbeschreibung: 'betriebliche Sicherheiten, keine Zusatzbesicherung',
}

// Recursively fill {{placeholders}} in every string field, and drop the now
// misleading "(kundenspezifisch auszufüllen)" hint from the affected titles.
function fillDemo<T>(node: T): T {
  if (typeof node === 'string') {
    return node
      .replace(/\{\{(\w+)\}\}/g, (_m, key: string) => DEMO_FILL[key] ?? `{{${key}}}`)
      .replace(/\s*\(kundenspezifisch auszufüllen\)/g, '') as unknown as T
  }
  if (Array.isArray(node)) return node.map(fillDemo) as unknown as T
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(node as Record<string, unknown>)) {
      out[key] = fillDemo((node as Record<string, unknown>)[key])
    }
    return out as unknown as T
  }
  return node
}

// Cast through unknown: importing JSON widens literals (e.g. each block `type`
// becomes `string`), which doesn't structurally match the discriminated-union
// Block types until asserted.
const data = raw as unknown as {
  analysis?: Section[]
  proposal?: Section[]
  roi?: Section[]
  financing?: Section[]
}

export const DEMO_HOTEL_REPORT: AnalysisReport = {
  cover: {
    subtitle: 'Energieanalyse Hotel A (anonymisiert)',
  },
  analyse: fillDemo(data.analysis ?? []),
  vorschlag: fillDemo(data.proposal ?? []),
  roi: fillDemo(data.roi ?? []),
  finanzierung: fillDemo(data.financing ?? []),
}
